import produce from 'immer';
import {Spec} from 'immutability-helper';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  allowPlateDesigns,
  enablePlateDesignContentsLayer,
} from 'notebook/integrations/plate-maps/FixedPlateEditorUtil';
import {NoteLinkedPlateDesignNotePart} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignTypes';
import {
  getAllPlateDesignNames,
  getPlateDesignName,
  copyNoteLinkedPlateDesign,
} from 'notebook/integrations/plate-maps/plate-design/NoteLinkedPlateDesignUtil';
import PlateDesignSummary from 'notebook/integrations/plate-maps/plate-design/PlateDesignSummary';
import {PLATE_MAP_PLUGIN_VIRTUALIZED_HEIGHT} from 'notebook/integrations/plate-maps/PlateMapPluginConstants';
import MediocrePlugins from 'notebook/mediocre/base-plugin/plugins';
import {MediocreItemAction} from 'notebook/mediocre/EditorTypes';
import {MediocreSearchResultProps} from 'notebook/mediocre/search/SearchResultProps';
import SelectableNotePart from 'notebook/mediocre/selection/block/SelectableNotePart';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteEditor, NoteEditorPlugin, NoteEditorProps} from 'notebook/notes/NoteEditorTypes';
import {NotePart} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import {PlateDesignVersionRef} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import APIUtil from 'util/APIUtil';
import {ClipboardItemType} from 'util/clipboard/ClipboardUtil';

/**
 * Props returned by component.getItemProps function in the plugin.
 */
export interface PlateDesignItemProps {
  plateDesignVersionRef: PlateDesignVersionRef | null;
  noteLinkedPlateDesignUuid: string | undefined;
  openVersionDbId: number | undefined;
}

function getPlateDesignVersionRef(
  editor: NoteEditor,
  notePart: NoteLinkedPlateDesignNotePart
): PlateDesignVersionRef | null {
  const plateDesignApiId = editor.props.getIdFromCid(notePart.item.data.cid);
  const plateDesignVersionApiId = editor.props.getIdFromCid(notePart.item.data.versionCid);
  return plateDesignApiId != null && plateDesignVersionApiId != null
    ? {plateDesignApiId, plateDesignVersionApiId}
    : null;
}

const NOTE_LINKED_PLATE_DESIGN_PLUGIN: NoteEditorPlugin = {
  type: ModelTypeConstants.NOTE_LINKED_PLATE_DESIGN,
  title: 'Plate Design',
  icon: 'tube-arrow-right',
  linkable: false,
  alignable: false,
  canPasteFromOtherTenant: false,
  component: {
    ...MediocrePlugins.selectablePluginBase,
    required: ['openFixedPlateEditorModal'],
    getItemProps(this: NoteEditor, notePart: NoteLinkedPlateDesignNotePart): PlateDesignItemProps {
      return {
        plateDesignVersionRef: getPlateDesignVersionRef(this, notePart),
        noteLinkedPlateDesignUuid: this.props.getIdFromCid(notePart.item.cid),
        openVersionDbId: this._getVersionId(this.props.fileId, APIUtil.apiIdToType(this.props.fileId)),
      };
    },
    make(
      this: NoteEditor,
      notePart: NoteLinkedPlateDesignNotePart,
      index: number,
      editorProps: NoteEditorProps,
      itemProps: PlateDesignItemProps,
      searchResultProps: MediocreSearchResultProps
    ): JSX.Element {
      return (
        <SelectableNotePart indentation={notePart.indentation}>
          <PlateDesignSummary
            fileId={editorProps.fileId}
            openVersionDbId={itemProps.openVersionDbId}
            locked={editorProps.locked}
            isProtected={editorProps.isProtected}
            viewer={editorProps.viewer}
            plateDesign={notePart.item.data.plateDesign}
            plateDesignVersionRef={itemProps.plateDesignVersionRef}
            noteLinkedPlateDesignUuid={itemProps.noteLinkedPlateDesignUuid}
            onEdit={(initiatingPlateDesignState) => {
              this.openFixedPlateEditorModal(index, {
                ...initiatingPlateDesignState,
                plateDesignNotePart: notePart,
              });
            }}
            onCreatePlateMap={(initiatingPlateDesignState) => {
              this.openFixedPlateEditorModal(index, {
                ...initiatingPlateDesignState,
                plateDesignNotePart: notePart,
              });
            }}
            hasPlateRecords={notePart.item.data.hasPlateRecords}
            getDesignClipboardItem={() => {
              const item = this.atIndex(index);
              const blobText = Promise.resolve(
                new Blob([this.processItemsForTextClipboard([item])], {type: ClipboardItemType.TEXT})
              );
              const blobHTML = Promise.resolve(
                new Blob([this.processItemsForHTMLClipboard([item])], {type: ClipboardItemType.HTML})
              );
              return new ClipboardItem({
                [ClipboardItemType.TEXT]: blobText,
                [ClipboardItemType.HTML]: blobHTML,
              });
            }}
          />
        </SelectableNotePart>
      );
    },
    getVirtualizedHeight: (item: NoteLinkedPlateDesignNotePart, isCollapsed: boolean) =>
      PLATE_MAP_PLUGIN_VIRTUALIZED_HEIGHT,
  },
  itemToText(this: NoteEditor, item: NoteLinkedPlateDesignNotePart): string {
    // TODO(BNCH-104149): This should be the text summary of the plate design.
    return getPlateDesignName(item.item.data.plateDesign);
  },
  itemToHTML(this: NoteEditor, $el: JQuery, notePart: NoteLinkedPlateDesignNotePart): JQuery {
    $el.attr(NoteConstants.COPY_NOTE_LINKED_OBJECT_DATA_PARENT_ID, this.props.fileId);
    return $el;
  },
  itemFromHTML(
    this: NoteEditor,
    $el: JQuery,
    notePart: NoteLinkedPlateDesignNotePart
  ): NoteLinkedPlateDesignNotePart | null {
    if (!allowPlateDesigns({fileId: this.props.fileId, isConfig: this.props.isConfig ?? false})) {
      notifications.warn('Plate designs are not currently supported in this context.', {
        id: 'xdoc-plate-design-paste',
      });
      return null;
    }

    const plateDesignVersionRef = getPlateDesignVersionRef(this, notePart);
    analytics.trackEventWithModule(ELN_ANALYTICS_ACTIONS.PASTED_PLATE_DESIGN, ANALYTICS_MODULE.ELN, {
      actionOrigin: this.isDocument() ? ActionOrigin.WORKSHEET : ActionOrigin.NOTEBOOK,
      plateDesignId: plateDesignVersionRef?.plateDesignApiId,
      plateDesignVersionId: plateDesignVersionRef?.plateDesignVersionApiId,
    });

    return notePart;
  },
  getContent(this: NoteEditor, {item}: NoteLinkedPlateDesignNotePart): never {
    // There is no shared code that extracts data from this note part.
    throw new Error('Cannot get content for plate designs.');
  },
  setContent(this: NoteEditor, data: unknown): Spec<NotePart> {
    // There is no shared code that writes data to this note part.
    throw new Error('Cannot set content for plate designs.');
  },
  getReasonForDisallowedAction(
    this: NoteEditor,
    item: NoteLinkedPlateDesignNotePart,
    action: MediocreItemAction
  ): string | null {
    // We may want to enforce that plate designs cannot be deleted (MediocreItemAction.DELETED) once they are
    // used in a transfer, i.e. have PlateRecords created from it. It is tricky to implement here,
    // unless the PlateDesignVersion captures this and includes it in the NotePart API representation.
    // Blocking here is necessary to provide a better UX than an unsavable document. This is not sufficient,
    // as we should also block on the backend PATCH as well as prevent resetting to an older version.
    return null;
  },
  convertItemForPaste(
    this: NoteEditor,
    item: NoteLinkedPlateDesignNotePart,
    destinationFileId: string,
    usedTableNames?: Array<string>,
    usedTextBoxNames?: Array<string>,
    isDestinationConfig?: boolean
  ): {addedItem: NotePart; oldName: string; newName: string} {
    const addedItem = produce(item, (draftItem) => {
      draftItem.item = copyNoteLinkedPlateDesign({
        source: item.item,
        existingPlateDesignNames: getAllPlateDesignNames(this.props.note),
        // If the contents layer is not enabled, we want to clear the contents and sources of the plate design.
        clearContentsAndSources: !enablePlateDesignContentsLayer({
          fileId: destinationFileId,
          isConfig: isDestinationConfig ?? false,
        }),
      });
    });
    const oldName = getPlateDesignName(item.item.data.plateDesign);
    const newName = getPlateDesignName(addedItem.item.data.plateDesign);
    return {addedItem, newName, oldName};
  },
};

export default NOTE_LINKED_PLATE_DESIGN_PLUGIN;
